// Utility helpers for transforming lists into filter dropdown option arrays.
// Centralizing this logic prevents duplication across grids and keeps future shape changes localized.

export interface NamedEntity {
  name: string;
}

export interface FilterOption {
  displayName: string;
  value: string;
  id: number;
}

/**
 * Maps an array of objects that contain a `name` property to the filter option shape.
 * - Null / undefined input returns an empty array
 * - Trims name and skips empty string names safely
 */
export function mapNamedListToFilterOptions<T extends NamedEntity>(list: T[] | null | undefined): FilterOption[] {
  if (!list || list.length === 0) {
    return [];
  }
  const result: FilterOption[] = [];
  for (let i = 0; i < list.length; i++) {
    const raw = list[i]?.name ?? '';
    const name = raw.trim();
    if (!name) continue; // skip empty/whitespace entries
    result.push({ displayName: name, value: name, id: i });
  }
  return result;
}

/**
 * Generic mapper allowing a custom projector for display + value when they differ from `name`.
 */
export function mapListToFilterOptions<T>(
  list: T[] | null | undefined,
  projector: (item: T, index: number) => { displayName: string; value: string }
): FilterOption[] {
  if (!list || list.length === 0) return [];
  const out: FilterOption[] = [];
  list.forEach((item, idx) => {
    const { displayName, value } = projector(item, idx);
    if (!displayName) return;
    out.push({ displayName, value, id: idx });
  });
  return out;
}

export function mapEntityIdListToFilterOptions<T extends { entityId: string }>(
  list: T[] | null | undefined,
  projector: (item: T) => string
): (FilterOption & { key: string })[] {
  if (!list || list.length === 0) return [];

  return list
    .map((item, index) => {
      const displayName = projector(item);
      return {
        displayName,
        value: item.entityId,
        id: index,
        key: item.entityId,
      };
    })
    .filter(option => option.displayName.length > 0);
}
